
import { useState } from 'react';
import { Star } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";

interface ReviewCarouselProps {
  reviews: string[];
}

export const ReviewCarousel = ({ reviews }: ReviewCarouselProps) => {
  const [expandedReviews, setExpandedReviews] = useState<Record<number, boolean>>({});
  
  const toggleExpand = (index: number) => {
    setExpandedReviews(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };
  
  // Function to limit text to approximately 5 lines
  const limitText = (text: string, expanded: boolean): string => {
    if (expanded) return text;
    
    // Approximate 5 lines based on character count (assuming ~70 chars per line)
    const charLimit = 350;
    if (text.length <= charLimit) return text;
    
    return text.substring(0, charLimit) + '...';
  };
  
  return (
    <Card className="border border-gray-200">
      <CardContent className="p-4">
        <h4 className="font-semibold mb-4">
          Bewertungen
        </h4>
        <Carousel className="w-full">
          <CarouselContent>
            {reviews && reviews.length > 0 ? (
              reviews.map((review, index) => (
                <CarouselItem key={index}>
                  <div className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="flex">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <Star key={i} className={`h-4 w-4 ${i < 4 ? "fill-yellow-400 stroke-yellow-400" : "stroke-gray-300"}`} />
                        ))}
                      </div>
                      <span className="text-sm font-medium">
                        Rezension {index + 1}
                      </span>
                    </div>
                    <div className="relative">
                      <p className="text-gray-600 text-sm">{limitText(review, !!expandedReviews[index])}</p>
                    </div>
                    {review.length > 350 && (
                      <Button 
                        variant="link" 
                        className="p-0 h-auto text-sm text-blue-600 mt-1"
                        onClick={() => toggleExpand(index)}
                      >
                        {expandedReviews[index] ? "Weniger" : "Mehr"}
                      </Button>
                    )}
                  </div>
                </CarouselItem>
              ))
            ) : (
              <CarouselItem>
                <div className="p-4">
                  <p className="text-gray-500 text-sm italic">
                    Keine Bewertungen verfügbar
                  </p>
                </div>
              </CarouselItem>
            )}
          </CarouselContent>
          <CarouselPrevious />
          <CarouselNext />
        </Carousel>
      </CardContent>
    </Card>
  );
};
